/**
 * App composition root. Provider order (R-18.200): ConfigProvider (theme) → AntApp
 * (message/notification context) → top-level ErrorBoundary (R-18.59) →
 * QueryClientProvider → AuthProvider → UserSettingsProvider → RouterProvider.
 * `AuthProvider` sits INSIDE `QueryClientProvider` (it runs the `/auth/me` query) and
 * OUTSIDE `RouterProvider` (routes + `<ProtectedRoute>` read auth state) per R-18.200.
 * `UserSettingsProvider` sits between them: it needs to know whether there is a caller
 * before it fetches, and the list pages under the router read their remembered page size
 * and column layout from it.
 *
 * The whole app is wrapped in `<Suspense>` so the initial i18n translation file load
 * shows a loading indicator (R-18.321). Theme switches between light/dark from the
 * theme store (R-18.18 — components never hardcode colours; the theme owns them).
 *
 * AntD chrome is localized (R-18.310): `ConfigProvider.locale` is bound to the active
 * i18n language so AntD's built-in strings (Table/pagination "x / page", prev/next,
 * empty-state, etc.) follow EN/LV. We subscribe to i18next's `languageChanged` so the
 * provider re-renders with the matching AntD locale pack on a language switch.
 */

import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { App as AntApp, ConfigProvider, Spin } from 'antd';
import enUS from 'antd/locale/en_US';
import lvLV from 'antd/locale/lv_LV';
import { Suspense, useMemo, useSyncExternalStore } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import { queryClient } from '@/app/query-client';
import { routes } from '@/app/routes';
import { AuthProvider } from '@/shared/auth/AuthProvider';
import { i18n } from '@/shared/lib/i18n';
import { useThemeStore } from '@/shared/stores/theme-store';
import { themes } from '@/shared/theme/theme';
import { AppErrorBoundary } from '@/shared/ui/AppErrorBoundary';
import { UserSettingsProvider } from '@/shared/user-settings';

// `antd/locale` only default-exports the locale VALUE, so derive the type from the import.
type AntdLocale = typeof enUS;

const ANTD_LOCALES: Record<string, AntdLocale> = { en: enUS, lv: lvLV };

/** Subscribe to i18next so the AntD locale re-renders on a language switch. */
function subscribeLanguage(onChange: () => void): () => void {
  i18n.on('languageChanged', onChange);
  return () => i18n.off('languageChanged', onChange);
}

export function App(): React.JSX.Element {
  const mode = useThemeStore((s) => s.mode);
  const router = useMemo(() => createBrowserRouter(routes), []);

  // Track the active i18n language (re-render on `languageChanged`) and map it to an AntD
  // locale pack; fall back to EN for any unmapped language.
  const language = useSyncExternalStore(subscribeLanguage, () => i18n.resolvedLanguage ?? 'en');
  const antdLocale = ANTD_LOCALES[language] ?? enUS;

  return (
    <ConfigProvider theme={themes[mode]} locale={antdLocale}>
      <AntApp>
        <AppErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <UserSettingsProvider>
                <Suspense fallback={<Spin fullscreen />}>
                  <RouterProvider router={router} />
                </Suspense>
              </UserSettingsProvider>
            </AuthProvider>
            {/* Devtools toggle pinned to the TOP-LEFT (Д-17). Its default corner is
                bottom-right, which is exactly where every drawer footer puts its primary
                button ("Confirm" / "Create intake" / "Next") — the floating button sits on
                top of them and eats the click, breaking manual testing and automation. The
                other three corners are not equivalent: bottom-right is the drawer footer,
                top-right is the header's account menu, and bottom-left is the tail of the
                sider menu once a long submenu (Admin) pushes it down to the viewport edge.
                Top-left is the only corner with nothing clickable under it — the sider's
                app-name label. The component self-disables outside a dev build (it returns
                null when NODE_ENV !== 'development'), so this is a dev-only surface. */}
            <ReactQueryDevtools initialIsOpen={false} buttonPosition="top-left" />
          </QueryClientProvider>
        </AppErrorBoundary>
      </AntApp>
    </ConfigProvider>
  );
}
